import { useState } from "react";
import {
  Box,
  Heading,
  Text,
  HStack,
  Grid,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Button,
  Divider,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { motion } from "framer-motion";

import api from '../services/api';

const MotionBox = motion(Box);

export default function ContactPage({ onOpenModal }) {
  const [formData, setFormData] = useState({
    fullname: "",
    email: "",
    phone: "",
    role: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    // Reset error state
    setError("");

    // Validate required fields
    if (!formData.fullname || !formData.email) {
      setError("Please fill in all required fields (Name and Email).");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    try {
      const response = await api.createContact(formData);
      console.log("Contact form response:", response);
      setSubmitted(true);
      // Reset form data
      setFormData({
        fullname: "",
        email: "",
        phone: "",
        role: "",
        message: "",
      });
    } catch (err) {
      console.error("Contact form error:", err);
      const errorMessage = err.response?.data?.detail || 
                          err.response?.data?.message || 
                          "An error occurred. Please try again later.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    { label: "Email", value: "info@reliabletouchservices.co.za" },
    { label: "Instagram", value: "@reliable_touch" },
    { label: "Office Hours", value: "Mon–Fri, 08:00 – 18:00" },
    { label: "Address", value: "98 8th Avenue, Edenvale, Johannesburg" },
    { label: "Phone", value: "063 956 8901 | 076 343 7491" },
  ];

  return (
    <Box
      id="contact"
      py={{ base: 24, md: 36 }}
      px={{ base: 6, md: 12, lg: 20 }}
      bg="brand.charcoal"
    >
      <Box maxW="1400px" mx="auto">
        {/* Header */}
        <MotionBox
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          mb={20}
        >
          <HStack mb={4} spacing={3}>
            <Box w="32px" h="1px" bg="brand.coral" />
            <Text
              fontSize="xs"
              letterSpacing="0.3em"
              textTransform="uppercase"
              color="brand.coral"
              fontWeight="600"
            >
              Contact
            </Text>
          </HStack>
          <Heading
            fontFamily="'Playfair Display', serif"
            fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
            fontWeight="400"
            color="brand.cream"
            lineHeight="1.2"
          >
            Let's Talk About
            <Text as="span" fontStyle="italic" color="brand.coral">
              {" "}
              Your Goals.
            </Text>
          </Heading>
        </MotionBox>

        <Grid
          templateColumns={{ base: "1fr", lg: "1fr 1fr" }}
          gap={{ base: 16, lg: 20 }}
        >
          {/* Form */}
          <MotionBox
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            {!submitted ? (
              <VStack spacing={5} align="stretch">
                {error && (
                  <Alert status="error" borderRadius="0" bg="rgba(220,38,38,0.1)" border="1px solid rgba(220,38,38,0.3)">
                    <AlertIcon color="brand.coral" />
                    <Text color="brand.coral" fontSize="sm">
                      {error}
                    </Text>
                  </Alert>
                )}
                <FormControl>
                  <FormLabel
                    fontSize="xs"
                    letterSpacing="0.2em"
                    textTransform="uppercase"
                    color="brand.muted"
                    mb={2}
                  >
                    Full Name
                  </FormLabel>
                  <Input
                    variant="dark"
                    placeholder="Jane Smith"
                    value={formData.fullname}
                    onChange={(e) =>
                      setFormData({ ...formData, fullname: e.target.value })
                    }
                    disabled={loading}
                  />
                </FormControl>

                <Grid templateColumns="1fr 1fr" gap={4}>
                  <FormControl>
                    <FormLabel
                      fontSize="xs"
                      letterSpacing="0.2em"
                      textTransform="uppercase"
                      color="brand.muted"
                      mb={2}
                    >
                      Email
                    </FormLabel>
                    <Input
                      variant="dark"
                      type="email"
                      placeholder="jane@example.com"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      disabled={loading}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel
                      fontSize="xs"
                      letterSpacing="0.2em"
                      textTransform="uppercase"
                      color="brand.muted"
                      mb={2}
                    >
                      Phone
                    </FormLabel>
                    <Input
                      variant="dark"
                      placeholder="+27 ..."
                      value={formData.phone}
                      onChange={(e) =>
                        setFormData({ ...formData, phone: e.target.value })
                      }
                      disabled={loading}
                    />
                  </FormControl>
                </Grid>

                <FormControl>
                  <FormLabel
                    fontSize="xs"
                    letterSpacing="0.2em"
                    textTransform="uppercase"
                    color="brand.muted"
                    mb={2}
                  >
                    I Am A
                  </FormLabel>
                  <Select
                    variant="dark"
                    placeholder="Select your role"
                    value={formData.role}
                    onChange={(e) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                    disabled={loading}
                  >
                    <option value="agent">Real Estate Agent</option>
                    <option value="landlord">Landlord</option>
                    <option value="buyer">Property Seeker</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel
                    fontSize="xs"
                    letterSpacing="0.2em"
                    textTransform="uppercase"
                    color="brand.muted"
                    mb={2}
                  >
                    Message
                  </FormLabel>
                  <Textarea
                    variant="dark"
                    placeholder="Tell us about your goals..."
                    rows={5}
                    value={formData.message}
                    onChange={(e) =>
                      setFormData({ ...formData, message: e.target.value })
                    }
                    borderRadius="0"
                    resize="vertical"
                    disabled={loading}
                  />
                </FormControl>

                <Button
                  variant="coral"
                  onClick={handleSubmit}
                  w="full"
                  h="52px"
                  mt={2}
                  isLoading={loading}
                  loadingText="Sending..."
                  disabled={loading}
                >
                  Send Message
                </Button>
              </VStack>
            ) : (
              <MotionBox
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                bg="brand.graphite"
                border="1px solid rgba(224,92,58,0.2)"
                p={12}
                textAlign="center"
              >
                <Text fontSize="5xl" mb={4}>
                  ✓
                </Text>
                <Heading
                  fontFamily="'Playfair Display', serif"
                  fontSize="2xl"
                  color="brand.coral"
                  mb={3}
                >
                  Message Sent!
                </Heading>
                <Text color="rgba(245,240,235,0.6)" lineHeight="1.8">
                  One of our professional agents will be in touch shortly.
                </Text>
              </MotionBox>
            )}
          </MotionBox>

          {/* Info Column */}
          <MotionBox
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <Box mb={12}>
              <Text
                fontFamily="'Playfair Display', serif"
                fontSize={{ base: "2xl", md: "3xl" }}
                fontStyle="italic"
                color="brand.cream"
                lineHeight="1.4"
              >
                "The voice behind
              </Text>
              <Text
                fontFamily="'Playfair Display', serif"
                fontSize={{ base: "2xl", md: "3xl" }}
                fontStyle="italic"
                color="brand.coral"
                lineHeight="1.4"
              >
                your next deal."
              </Text>
            </Box>

            <VStack align="start" spacing={0}>
              {contactInfo.map((item, i) => (
                <Box
                  key={i}
                  borderBottom="1px solid rgba(46,46,46,0.6)"
                  pb={5}
                  mb={5}
                  w="full"
                >
                  <Text
                    fontSize="xs"
                    letterSpacing="0.2em"
                    textTransform="uppercase"
                    color="brand.coral"
                    mb={1}
                    fontWeight="600"
                  >
                    {item.label}
                  </Text>
                  <Text color="brand.cream" fontSize="sm">
                    {item.value}
                  </Text>
                </Box>
              ))}
            </VStack>

            {/* Immediate Assistance */}
            {/* <Box
              mt={10}
              bg="brand.black"
              p={8}
              borderLeft="2px solid"
              borderColor="brand.coral"
            >
              <Text
                fontSize="xs"
                letterSpacing="0.2em"
                textTransform="uppercase"
                color="brand.coral"
                mb={5}
                fontWeight="600"
              >
                Immediate Assistance
              </Text>
              <VStack spacing={3}>
                <Button variant="coral" w="full" onClick={onOpenModal}>
                  I Want to Sell / Lease
                </Button>
                <Button variant="ghostCoral" w="full" onClick={onOpenModal}>
                  I Want to Buy / Rent
                </Button>
              </VStack>
            </Box> */}
          </MotionBox>
        </Grid>
      </Box>
    </Box>
  );
}
